import React, { useState, useEffect, useMemo } from 'react';
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   PointElement,
   Title,
   Tooltip as ChartTooltip,
   Legend,
   ArcElement
} from 'chart.js';
import { Box, Container, Grid, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { dataPieChar, subDataPie } from '../../utils/types/pieChart.types';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';

// Registrar los componentes necesarios
ChartJS.register(CategoryScale, LinearScale, PointElement, Title, ChartTooltip, Legend, ArcElement);

interface PieDateChartProps {
   titleLevel: Record<number, string>;
   data: dataPieChar[];
   defaultSlider?: { start: number; end: number };
   categories: Record<number, Record<string, string>>;
   minLevel?: number;
   maxLevel?: number;
}

interface Mark {
   value: number;
   label: string;
}

export const PieChartMultiChart: React.FC<PieDateChartProps> = ({
   titleLevel,
   data,
   categories,
   defaultSlider = { end: 4, start: 0 },
   minLevel = 0,
   maxLevel = 0,
}) => {
   // Estado del Slider
   const [valueSlider, setValueSlider] = useState<[number, number]>([
      defaultSlider.start,
      defaultSlider.end
   ]);
   const [marks, setMarks] = useState<Mark[]>([]);
   const [monthLabels, setMonthLabels] = useState<string[]>([]);
   const [filterLevel, setFilterLevel] = useState<Record<number, Set<number>>>({});

   // Estado para los datos
   const [dataViewChart, setDataViewChart] = useState<subDataPie[]>([]);

   const handleSliderChange = (event: Event, newValue: number | number[]) => {
      const [mesInicial, mesFinal] = newValue as [number, number];
      setValueSlider([mesInicial, mesFinal]);
   };

   const [nivelView, setNivelView] = useState<number>(minLevel);

   const handleChangeLevel = ({
      prevLevel,
      newLevel,
      codColumn
   }: {
      prevLevel: number;
      newLevel: number;
      codColumn?: string;
   }) => {
      let filterPrevLevel: Set<number | string>;

      const [start, end] = valueSlider;

      // Nivel 0: filtro por mes
      const mesesSeleccionados = data.slice(start, end + 1).map(value => value.mes);

      const sliderFilter: Set<number> = new Set(mesesSeleccionados);

      const copyFilter: Record<number, Set<any>> = {};
      for (const [levelStr, set] of Object.entries(filterLevel)) {
         const level = Number(levelStr);
         if (level < newLevel) {
            copyFilter[level] = set;
         }
      }

      const newFilterLevels: Record<number, Set<any>> = {
         ...copyFilter,
         0: sliderFilter
      };

      // Filtro por etiqueta (niveles > 0)
      if (codColumn) {
         filterPrevLevel = new Set([codColumn]);
         newFilterLevels[prevLevel] = filterPrevLevel;
      }

      let lastDataLevel: dataPieChar[] = [...data];
      let currLevelProcess = 0;
      let lastSubData: subDataPie[] = [];

      while (newLevel + 1 > currLevelProcess) {
         const filterNextLevel: Record<string, subDataPie[]> = {};

         // Filtrar y juntar niveles
         lastDataLevel.forEach(currRow => {
            const currFilter = newFilterLevels[currLevelProcess];
            let insertData = false;

            if (currFilter) {
               if (
                  currLevelProcess === 0
                     ? currFilter.has(currRow.mes) // Nivel 0 usa 'mes' como criterio
                     : currFilter.has(currRow.mark) // Niveles superiores usan 'mark'
               ) {
                  insertData = true;
               }
            } else {
               insertData = true;
            }

            if (insertData && currRow.subData?.length) {
               if (!filterNextLevel[currRow.mark]) {
                  filterNextLevel[currRow.mark] = [];
               }
               filterNextLevel[currRow.mark] =
                  filterNextLevel[currRow.mark].concat(currRow.subData);
            }
         });

         // Reducir a únicos (acumular por etiqueta)
         const acummColumns: Record<string, subDataPie> = {};

         Object.values(filterNextLevel).forEach(value => {
            value.forEach(currRow => {
               const newValue: subDataPie = acummColumns[currRow.etiqueta] ?? {
                  etiqueta: currRow.etiqueta,
                  value: 0,
                  subData: []
               };

               newValue.value += currRow.value;
               newValue.subData = (newValue.subData ?? []).concat(
                  currRow.subData ?? []
               );

               acummColumns[currRow.etiqueta] = newValue;
            });
         });

         lastSubData = Object.values(acummColumns);

         // Reconstruir dataPieChar para el siguiente nivel
         lastDataLevel = Object.values(acummColumns).map(sub => ({
            mes: currLevelProcess === 0
               ? lastDataLevel.find(row => row.mark === sub.etiqueta)?.mes ?? 0
               : lastDataLevel.find(row => row.mark === sub.etiqueta)?.mes ?? 0, // Propaga mes desde el primer gráfico
            mark: sub.etiqueta,
            denominacion: sub.etiqueta,
            subData: sub.subData ?? []
         }));
         currLevelProcess += 1;
      }
      setFilterLevel(newFilterLevels);
      setNivelView(newLevel);
      setDataViewChart(lastSubData)
   };

   const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
         tooltip: {
            backgroundColor: '#fff',
            titleColor: '#333',
            bodyColor: '#333',
            borderColor: '#ccc',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: true,
            padding: 8,
            boxPadding: 6,
            boxWidth: 10,
            boxHeight: 10
         }
      }
   };

   // Calculamos los datos del gráfico
   const chartData = useMemo(() => {
      const currentCategories = categories[nivelView];
      const dataForChart = Object.keys(currentCategories).map(category => dataViewChart.find(data => data.etiqueta == category)?.value || 0);
      return {
         labels: Object.values(currentCategories),
         datasets: [
            {
               label: '# de llaves',
               data: dataForChart,
               backgroundColor: [
                  'rgba(255, 99, 132, 0.2)',
                  'rgba(54, 162, 235, 0.2)',
                  'rgba(75, 192, 192, 0.2)',
                  'rgba(255, 159, 64, 0.2)',
                  'rgba(153, 102, 255, 0.2)'
               ],
               borderColor: [
                  'rgba(255, 99, 132, 1)',
                  'rgba(54, 162, 235, 1)',
                  'rgba(75, 192, 192, 1)',
                  'rgba(255, 159, 64, 1)',
                  'rgba(153, 102, 255, 1)'
               ],
               borderWidth: 1
            }
         ]
      };
   }, [dataViewChart]);

   // Actualizamos los datos según el slider
   useEffect(() => {
      if (data && data.length > 0) {
         handleChangeLevel({ newLevel: nivelView, prevLevel: nivelView });
      }
   }, [valueSlider]);

   useEffect(() => {
      if (data) {
         const newMarks = data.map((currRow, index) => ({
            value: index,
            label: currRow.mark
         }));
         const newLabels: string[] = data.map(currRow => currRow.denominacion);
         setMarks(newMarks);
         setMonthLabels(newLabels);
         if (data.length > 0) handleChangeLevel({ newLevel: nivelView, prevLevel: minLevel });
      }
   }, [data]);

   return (
      <Container sx={{ height: '100%', maxHeight: '80vh' }}>
         <Grid item container>
            <Grid item xs={12}>
               <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box width='72px'></Box>
                  {/* Título centrado */}
                  <Typography variant="h5" mt={0.5} textAlign="center" lineHeight={1.1}>
                     {titleLevel[nivelView] ?? ''}
                  </Typography>
                  {/* Iconos de orden (derecha) */}
                  <Box display="flex" gap={1}>
                     <Tooltip
                        title={nivelView <= 0 ? '' : 'Gráfico Anterior'}
                        placement="top"
                        arrow
                     >
                        <span>
                           <IconButton
                              onClick={() => {
                                 handleChangeLevel({
                                    newLevel: nivelView - 1,
                                    prevLevel: nivelView
                                 });
                              }}
                              sx={{
                                 p: 0.5,
                                 '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.main'
                                 }
                              }}
                              disabled={nivelView <= minLevel}
                           >
                              <NorthIcon fontSize="medium" />
                           </IconButton>
                        </span>
                     </Tooltip>

                     <Tooltip
                        title={nivelView >= maxLevel ? '' : 'Siguiente Gráfico'}
                        placement="top"
                        arrow
                     >
                        <span>
                           <IconButton
                              onClick={() => {
                                 handleChangeLevel({
                                    newLevel: nivelView + 1,
                                    prevLevel: nivelView
                                 });
                              }}
                              sx={{
                                 p: 0.5,
                                 '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.main'
                                 }
                              }}
                              disabled={nivelView >= maxLevel}
                           >
                              <SouthIcon fontSize="medium" />
                           </IconButton>
                        </span>
                     </Tooltip>
                  </Box>
               </Box>
            </Grid>
         </Grid>
         <Grid item xs={12} sx={{ height: 'calc(100% - 100px)' }}>
            <Pie data={chartData} options={options} />
         </Grid>
         <Grid item xs={12}>
            <Box style={{ width: '90%', margin: 'auto' }}>
               <Slider
                  value={valueSlider}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={marks.length - 1}
                  marks={marks}
                  step={1}
                  valueLabelFormat={index => monthLabels[index]}
                  style={{ fontSize: '5px important!' }}
               />
            </Box>
         </Grid>
      </Container>
   );
};
